import React from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import CustomDialog from "../../../Components/common/Dialog";
import { ReactComponent as ArrowUp } from "../../../assets/dashboard/arrow-up.svg";
import { ReactComponent as ArrowDown } from "../../../assets/dashboard/arrow-down.svg";
import { ReactComponent as Rectangle } from "../../../assets/dashboard/rectangle.svg";

const getDiffColor = (diff) => {
  if (diff > 0) return "#14C52F";
  if (diff < 0) return "#E45454";
  return "#E4D354";
};

const parseHHMMSS = (timeStr) => {
  if (!timeStr) return { hours: 0, minutes: 0, seconds: 0, totalMinutes: 0, raw: "0 MIN" };
  const [h, m, s] = timeStr.split(":").map(Number);
  const totalMinutes = h * 60 + m + s / 60;
  return { hours: h, minutes: m, seconds: s, totalMinutes, raw: timeStr };
};

// Format minutes to "Xh Ym"
const formatIdleDisplay = (minutes) => {
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  return `${Math.round(minutes)} min`;
};


const getIdleColor = (minutes, benchmarkMinutes) => {
  if (minutes < benchmarkMinutes) return "#14C52F"; // green → under benchmark, okay
  if (minutes > benchmarkMinutes) return "#E45454"; // red → over benchmark, load
  return "#E4D354"; // yellow → exactly at benchmark
};


// const MAX_IDLE_MINUTES = 600;
export default function DetailModal({ open, onClose, data, themecolors }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!open || !data) return null;

  return (
    <CustomDialog
      onClose={onClose}
      maxWidth="md"
      fullScreen={isMobile}
      disableFooter
      noFullScreen
      bodyPadding="0"
      overflow="auto"
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <img
            src={data.category.icon}
            alt={data.category.name}
            width={isMobile ? 40 : 50}
          />
          <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
            {data.category.name} | {data.category.count}
          </Typography>
        </Box>
      }
      themecolors={{
        ...themecolors,
        themeLightColor: themecolors?.menuActiveBackground || "#0a83a1",
        themeLightInverse: themecolors?.textColor || "white",
        backgroundColor: themecolors?.menuActiveBackground || "#0a83a1",
        textColor: themecolors?.textColor || "white",
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {Object.entries(data.metrics).map(([name, metric]) => {
            const isAvgIdle = name === "Avg Idle";
            const diff = metric.value - metric.benchmark;

            const idle = isAvgIdle ? parseHHMMSS(metric.value) : null;
            const benchmark = isAvgIdle ? parseHHMMSS(metric.benchmark) : null;

           const percentage = isAvgIdle
  ? benchmark.totalMinutes > 0
    ? (idle.totalMinutes / benchmark.totalMinutes) * 100
    : 0
  : Number(metric.value) || 0;
const barWidth = Math.min(Math.max(percentage, 0), 100);
const totalMinutes = idle?.totalMinutes - benchmark?.totalMinutes;

const absMinutes = Math.abs(totalMinutes);
const hours = Math.floor(absMinutes / 60);
const minutes = absMinutes % 60;

const sign = totalMinutes < 0 ? "-" : "";
const result = minutes > 0 ? `${sign}${hours}h ${parseInt(minutes)}m` : `${sign}${hours}h`;


              
            return (
              <Box
                key={name}
                sx={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "200px 1fr 140px",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box>
                      <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
                        {isAvgIdle
                          ? formatIdleDisplay(idle.totalMinutes)
                          : `${Number(metric.value).toFixed(2)}%`}
                      </Typography>
                      <Typography sx={{ fontSize: 16 }}>{name}</Typography>
                    </Box>
                  </Box>

           
<Box
  sx={{
    display: "flex",
    alignItems: "center",
    gap: 0.5,
  }}
>

{isAvgIdle && (
  <>
    {idle.totalMinutes < benchmark.totalMinutes ? (
      <Rectangle style={{ color: getIdleColor(idle.totalMinutes, benchmark.totalMinutes) }} />
    ) : idle.totalMinutes > benchmark.totalMinutes ? (
      <Rectangle style={{ color: getIdleColor(idle.totalMinutes, benchmark.totalMinutes) }} />
    ) : (
      <Rectangle style={{ color: getIdleColor(idle.totalMinutes, benchmark.totalMinutes) }} />
    )}
  </>
)}


  {/* Show diff arrows only for non-Avg Idle metrics */}
  {!isAvgIdle && (
    <>
      {diff > 0 ? (
        <ArrowUp style={{ color: getDiffColor(diff) }} />
      ) : diff < 0 ? (
        <ArrowDown style={{ color: getDiffColor(diff) }} />
      ) : (
        <Rectangle style={{ color: getDiffColor(diff) }} />
      )}
    </>
  )}

  <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
    {isAvgIdle
      ? `${result}` 
      : `${Math.abs(diff).toFixed(2)}%`}
  </Typography>
</Box>

                </Box>
                <Tooltip
                  title={
                    isAvgIdle? `Exact time: ${idle.raw} | ${percentage.toFixed(2)}%` : `${percentage.toFixed(2)}%`
                  }
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: 32,
                      bgcolor: "rgba(0,0,0,0.25)",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${barWidth}%`,
                        bgcolor: isAvgIdle ? getIdleColor(idle.totalMinutes,benchmark.totalMinutes) : getDiffColor(diff),
                        transition: "width 0.3s ease",
                      }}
                    />
                  </Box>
                </Tooltip>
                <Typography sx={{ fontSize: 14, fontStyle: "italic" }}>
                  Benchmark{" "}
                  <strong>
                    {isAvgIdle
                      ? formatIdleDisplay(benchmark.totalMinutes)
                      : `${metric.benchmark}%`}
                  </strong>
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </CustomDialog>
  );
}